import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchSingleDispute } from "../../../redux/features/orderSlice";
import {
  BadgeAlert,
  BadgeCheck,
  CopyIcon,
  MessageSquareIcon,
} from "lucide-react";
import ProductCard from "./DisputedItem";
import moment from "moment";
import ApproveDisputeModal from "./ApproveDisputeModal";
import AppealModal from "./ViewAppealMessage";
import { copyToClipboard, formatNumberWithCommas } from "../../../helpers";

const statusOptions: any = {
  PENDING_REVIEW: {
    label: "Pending review",
    color: "bg-red-100 text-red-600 border-red-600",
    icon: <BadgeAlert className="w-4 h-4 mr-1" />,
  },
  UNDER_REVIEW: {
    label: "Under review",
    color: "bg-indigo-100 text-indigo-600 border-indigo-600",
    icon: <BadgeAlert className="w-4 h-4 mr-1" />,
  },
  RESOLVED: {
    label: "Resolved",
    color: "bg-green-100 text-green-500 border border-green-500",
    icon: <BadgeCheck className="w-4 h-4 mr-1" />,
  },
};

const DisputedOrderDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedDispute } = useAppSelector((state) => state.orders);
  const [openApproveDisputeModal, setOpenApproveDisputeModal] = useState(false);
  const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    dispatch(fetchSingleDispute(id));
  }, [id, dispatch]);

  return (
    <div className="p-6">
      {/* Back Button */}
      <button
        className="text-gray-500 hover:text-gray-800 flex items-center font-bold mb-4 bg-gray-50 p-2 rounded"
        onClick={() => window.history.back()}
      >
        &larr; Back
      </button>

      <div className="bg-white shadow-sm rounded-lg p-4 md:p-6 w-full">
        {/* Order ID and Status */}
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-semibold">
            INT{selectedDispute?.order?.contract?.contractCode}
          </h2>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <h1 className="text-base sm:text-lg">
                {selectedDispute?.disputeStatus !== "RESOLVED"
                  ? "This order is currently in dispute"
                  : "This order is no longer in dispute"}
              </h1>
              <span
                className={`flex items-center gap-2 ${
                  statusOptions[selectedDispute?.disputeStatus]?.color
                } text-sm font-medium px-3 py-1 rounded-md border`}
              >
                {statusOptions[selectedDispute?.disputeStatus]?.icon}
                {statusOptions[selectedDispute?.disputeStatus]?.label}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <button className="text-gray-700 px-4 py-2 rounded-full text-sm border border-[#B3B3B3] w-full sm:w-auto">
                Export report, CSV, PDF
              </button>
              {selectedDispute?.disputeStatus === "PENDING_REVIEW" && (
                <button
                  className="bg-primary text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-indigo-700 w-full sm:w-auto"
                  onClick={() => setOpenApproveDisputeModal(true)}
                >
                  Review this dispute
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedDispute?.disputeStatus === "UNDER_REVIEW" && (
        <div className="bg-white shadow-sm rounded-lg p-5 w-full mt-6">
          <div className="flex item-center justify-between w-full">
            <div>
              <span className="text-md text-gray-500">Return deadline: </span>
              <span className="text-black text-md font-bold">
                31st july, 2025
              </span>
            </div>
            <div className="border-r border-r-[#D2D2D2]"></div>
            <div>
              <span className="text-md text-gray-500">Days remaining: </span>
              <span className="text-black text-md font-bold">45 days</span>
            </div>
            <div className="border-r border-r-[#D2D2D2]"></div>
            <div>
              <span className="text-md text-red-500">Return balance: </span>
              <span className="text-red-500 text-md font-bold">
                NGN{" "}
                {formatNumberWithCommas(selectedDispute?.order?.orderAmount)}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 mb-6">
        {/* Combined dispute order details and dispute status Section */}
        <div className="col-span-2 lg:col-span-1 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          {/* dispute order details Section */}
          <h3 className="font-semibold text-lg mb-4 text-[#6F6F6F]">
            Disputed order details
          </h3>
          <div className="border-t my-6"></div>
          <div className="space-y-4">
            {[
              {
                label: "Contract code",
                value: selectedDispute?.order?.contract?.contractCode ?? "N/A",
              },
              {
                label: "Purchase date",
                value: selectedDispute?.order?.createdAt
                  ? moment(selectedDispute?.order?.createdAt).format(
                      "Do MMM YYYY"
                    )
                  : "N/A",
              },
              {
                label: "Item in dispute",
                value:
                  selectedDispute?.order?.contract?.contractItem?.length +
                  " Item(s)",
              },
              {
                label: "Name of buyer",
                value: (
                  <span className="flex item-center gap-2">
                    {selectedDispute?.order?.contract?.buyerId?.fullName ??
                      "N/A "}
                    <CopyIcon
                      className="text-accent cursor-pointer"
                      size={14}
                      onClick={() =>
                        copyToClipboard(
                          selectedDispute?.order?.contract?.buyerId?.fullName
                        )
                      }
                    />
                  </span>
                ),
              },
              {
                label: "Name of vendor",
                value: (
                  <span className="flex item-center gap-2">
                    {selectedDispute?.order?.contract?.vendorId?.fullName ??
                      "N/A "}
                    <CopyIcon
                      className="text-accent cursor-pointer"
                      size={14}
                      onClick={() =>
                        copyToClipboard(
                          selectedDispute?.order?.contract?.vendorId?.fullName
                        )
                      }
                    />
                  </span>
                ),
              },
              {
                label: "Amount",
                value: "₦" + selectedDispute?.order?.orderAmount || "N/A",
              },
            ].map((detail, index) => (
              <div key={index} className="flex justify-between text-sm">
                <p className="text-[#393939] font-bold">{detail.label}</p>
                <p className="font-medium text-[#6F6F6F]">{detail.value}</p>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t my-6"></div>
        </div>

        <div className="col-span-2 lg:col-span-1 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          {/* dispute order status Section */}
          <h3 className="font-semibold text-lg mb-4 text-[#6F6F6F]">
            Disputed status
          </h3>
          <div className="border-t my-6"></div>
          <div className="space-y-4">
            {[
              {
                label: "Status",
                value: (
                  <span
                    className={`flex items-center  ${
                      statusOptions[`${selectedDispute?.disputeStatus}`].color
                    } text-xs font-medium px-3 py-1 rounded-md border `}
                  >
                    {statusOptions[`${selectedDispute?.disputeStatus}`].icon}{" "}
                    {statusOptions[`${selectedDispute?.disputeStatus}`].label}
                  </span>
                ),
              },
              {
                label: "Dispute reason",
                value: selectedDispute?.question?.disputeReason?.name ?? "N/A",
              },
              {
                label: "Submission date",
                value:
                  moment(
                    selectedDispute?.question?.disputeReason?.createdAt
                  ).format("Do MMM YYYY") ?? "N/A",
              },
              {
                label: "Expected resolution time",
                value: selectedDispute?.finalDecisionDate
                  ? moment(selectedDispute?.finalDecisionDate).format(
                      "Do MMM YYYY"
                    )
                  : "N/A",
              },
            ].map((detail, index) => (
              <div key={index} className="flex justify-between text-sm">
                <p className="text-[#393939] font-bold">{detail.label}</p>
                <p className="font-medium text-[#6F6F6F]">{detail.value}</p>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t my-6"></div>
        </div>
      </div>

      {/* {selectedDispute?.disputeStatus === "RESOLVED" && ( */}
      <div className="bg-white shadow-sm rounded-lg p-5  w-full mb-6 ">
        <h3 className="font-semibold text-lg mb-4 text-[#6F6F6F]">
          Appeal Information
        </h3>
        <div className="border-t my-6"></div>

        <div className="flex justify-between items-start gap-10 flex-col sm:flex-row">
          <div className="w-full sm:flex-1">
            <div className="space-y-4">
              {[
                {
                  label: "Appeal",
                  value: selectedDispute?.appeal ?? "N/A",
                },
                {
                  label: "Appeal Date",
                  value: selectedDispute?.appealDate
                    ? moment(selectedDispute?.appealDate).format("Do MMM YYYY")
                    : "N/A",
                },
                {
                  label: "Final Decision Date",
                  value: selectedDispute?.finalDecisionDate
                    ? moment(selectedDispute?.finalDecisionDate).format(
                        "Do MMM YYYY"
                      )
                    : "N/A",
                },
                {
                  label: "Final Decision Reason",
                  value: selectedDispute?.finalDecisionReason ?? "N/A",
                },
              ].map((detail, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <p className="text-[#393939] font-bold">{detail.label}</p>
                  <p className="font-medium text-[#6F6F6F]">{detail.value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full sm:flex-1">
            {" "}
            <div className="space-y-4">
              {[
                {
                  label: "Review Defaulter",
                  value: selectedDispute?.reviewDefaulter ?? "N/A",
                },
                {
                  label: "Final Defaulter",
                  value: selectedDispute?.finalDefaulter ?? "N/A",
                },
                {
                  label: "Review Result Reason",
                  value: selectedDispute?.reviewResultReason ?? "N/A",
                },
                {
                  label: "Review Result Date",
                  value: selectedDispute?.finalDecisionDate
                    ? moment(selectedDispute?.finalDecisionDate).format(
                        "Do MMM YYYY"
                      )
                    : "N/A",
                },
              ].map((detail, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <p className="text-[#393939] font-bold">{detail.label}</p>
                  <p className="font-medium text-[#6F6F6F]">{detail.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* )} */}

      {selectedDispute?.disputeStatus === "UNDER_REVIEW" && (
        <div className="bg-white shadow-sm rounded-lg p-5 w-full mb-6">
          <div className="flex items-center gap-6">
            <MessageSquareIcon color="#2C14DD" />
            {selectedDispute?.appeal ? (
              <span
                className="text-md text-accent cursor-pointer"
                onClick={() => setIsAppealModalOpen(true)}
              >
                View Appeal Message
              </span>
            ) : (
              <span className="text-md text-red-500 cursor-pointer">
                {selectedDispute?.reviewDefaulter.toLowerCase() === "buyer"
                  ? "Buyer"
                  : "Vendor"}{" "}
                has not appealed yet!
              </span>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-4 rounded-lg shadow-sm">
        {selectedDispute?.order?.contract?.contractItem?.map((item: any) => (
          <ProductCard
            image={item?.productId?.photos[0]}
            title={item?.productId?.name}
            price={`₦${item?.productId?.price}`}
            color={item?.productId?.colors ?? "N/A"}
          />
        ))}
      </div>

      <ApproveDisputeModal
        isOpen={openApproveDisputeModal}
        onClose={() => setOpenApproveDisputeModal(false)}
        modalTitle="Review Dispute"
      />
      <AppealModal
        isOpen={isAppealModalOpen}
        onClose={() => setIsAppealModalOpen(false)}
      />
    </div>
  );
};

export default DisputedOrderDetails;
